import React from 'react';
import transition from '../../transition';
import CaseStudy from '../CaseStudy/CaseStudy';
import '../Project.css';

import hero from '../../assets/images/projects/qf-wellness/qf-wellness-hero.png';
import QFW1 from '../../assets/images/projects/qf-wellness/mobileQFW01.png';
import QFW2 from '../../assets/images/projects/qf-wellness/mobileQFW02.png';
import QFW3 from '../../assets/images/projects/qf-wellness/desktopQFW01.png';
import QFW4 from '../../assets/images/projects/qf-wellness/desktopQFW02.png';
import QFW5 from '../../assets/images/projects/qf-wellness/desktopQFW03.png';
import QFW6 from '../../assets/images/projects/qf-wellness/desktopQFW04.png';

const QFWellness = () => {
  return (
    <CaseStudy
      className="qfwellness"
      accent="var(--qfwellness)"
      eyebrow="Product · Health education"
      title="QF Wellness"
      summary="An online learning platform I built for Quintana Functional Wellness so women with Hashimoto’s can take courses, track progress, and pay securely in one place."
      links={[
        {
          href: 'https://quintanafunctionalwellness.com/',
          label: 'View live site'
        },
        {
          href: '#frames',
          label: 'View frames'
        }
      ]}
      hero={{
        src: hero,
        alt: 'Quintana Functional Wellness desktop and mobile composition',
        caption: 'Quintana Functional Wellness · site + mobile'
      }}
      date="June – July 2024"
      role="Sole developer: product, UI, and integrations"
      stack={['Next.js', 'React.js', 'MySQL', 'Stripe', 'JavaScript']}
      brief={{
        label: 'Background',
        they: {
          title: 'The practice',
          body: 'Quintana Functional Wellness helps women managing Hashimoto’s thyroiditis through functional-medicine education. The centerpiece is a 12-week Hashimoto’s recovery blueprint: resources, lessons, and a clear plan people can follow from home.'
        },
        me: {
          title: 'My role',
          body: 'I was the sole developer. I designed and built the site and course platform in Next.js, including accounts, a custom CMS, Stripe checkout, progress tracking, and analytics so the practice could run the program without a separate stack of tools.'
        }
      }}
      problem={{
        title: 'A serious health program stuck without a real product home.',
        lead: 'The practice needed more than a brochure site. Students needed login, lessons, payments, and progress in one place.',
        detail:
          'Hashimoto’s care here is structured education, not a one-off blog post. That means accounts, course content that can change, secure checkout, and a way for the practice to see what students are actually completing. Piecing that together from generic website builders and scattered tools would have been fragile and hard to own.'
      }}
      metrics={[
        { value: '12-wk', label: 'Recovery blueprint program' },
        { value: 'CMS', label: 'Content owned by the practice' },
        { value: 'Stripe', label: 'Checkout built in' },
        { value: 'Next.js', label: 'App + site in one stack' }
      ]}
      outcome="A single platform for marketing, courses, payments, and progress — built so the practice can update content without waiting on a developer."
      solutionsLabel="What shipped"
      solutions={[
        {
          title: 'Public site',
          lead: 'A clear front door for the practice and the Hashimoto’s program.',
          detail:
            'Built in Next.js so the marketing pages and the logged-in product share one codebase. Mobile navigation and layout were first-class, not an afterthought, because most people will find this from a phone.',
          images: [
            {
              src: QFW6,
              alt: 'Quintana Functional Wellness homepage',
              caption: 'Homepage for the practice and program.'
            },
            {
              src: QFW2,
              alt: 'Homepage on mobile',
              caption: 'Same story on mobile.'
            },
            {
              src: QFW1,
              alt: 'Mobile navigation',
              caption: 'Mobile navigation into courses and resources.'
            }
          ]
        },
        {
          title: 'Courses, accounts, and checkout',
          lead: 'Students can sign in, purchase, and work through the program in one flow.',
          detail:
            'Google, Facebook, and Apple login lower the barrier to start. Stripe handles payment. Once inside, students can view courses and track progress instead of bouncing between email links and a separate payment page.',
          images: [
            {
              src: QFW5,
              alt: 'Media and course content page',
              caption: 'Course and media content pages.'
            },
            {
              src: QFW4,
              alt: 'Testimonials section',
              caption: 'Social proof woven into the public site.'
            }
          ]
        },
        {
          title: 'CMS for the practice',
          lead: 'A custom content system so program pages can change without a deploy.',
          detail:
            'The practice needed to update copy, images, and course material over time. The CMS is built around those pages, with analytics so they can see engagement and adjust the program instead of guessing.',
          images: [
            {
              src: QFW3,
              alt: 'CMS page with featured image',
              caption: 'CMS-driven pages with editable media.'
            },
            {
              src: QFW4,
              alt: 'Testimonials managed as content',
              caption: 'Sections the practice can keep current.'
            }
          ]
        }
      ]}
      decisions={[
        {
          title: 'One product, not a pile of tools',
          body: 'Marketing site, course access, payments, and CMS live together in Next.js so students and the practice are not juggling three vendors for one program.'
        },
        {
          title: 'Let the practice publish',
          body: 'A custom CMS mattered more than a pretty static site. Program content changes; waiting on a developer for every edit would kill momentum.'
        },
        {
          title: 'Checkout inside the experience',
          body: 'Stripe is integrated into the platform so buying the blueprint feels like joining the product, not leaving for a generic payment form.'
        },
        {
          title: 'Track progress on purpose',
          body: 'Progress tracking and analytics were part of the build so the practice can see what students finish and improve the 12-week program over time.'
        }
      ]}
      next={{ link: '/projects/diningwithtara', label: 'Dining with Tara' }}
    />
  );
};

export default transition(QFWellness);
